import { useCallback } from 'react';

import { QueryClient, QueryStatus, useQuery } from '@tanstack/react-query';

import { datasourceEndpoints } from '@/api/endpoints';
import { callGet } from '@/api/fetcher';
import { DatasourceDocument, DatasourceTab } from '@/modules/collections/types';
import { Pagination } from '@/shared/types/common';

import { queryKeyFactories } from '../../../hooks/utils/queryKeyFactory';
import { sortDatasourceDocuments } from '../helper';

/**
 * Type for the API response that contains datasources with document fields and pagination
 */
interface DatasourcesAndDocumentsResponse {
  documents: DatasourceDocument[];
  pagination: Pagination['pagination'];
}

/**
 * Hook parameters interface
 */
interface UseAPIDatasourcesDocumentsParams {
  collection_id: string;
  datasourceTab: DatasourceTab;
  enabled?: boolean;
}

/**
 * Hook return interface
 */
interface UseAPIDatasourcesDocumentsReturn {
  data: DatasourceDocument[];
  pagination: Pagination['pagination'] | undefined;
  isDatasourcesDocumentsLoading: boolean;
  getDatasourceDocumentById: (document_id: string) => DatasourceDocument | undefined;
  getStatus: QueryStatus;
}

/**
 * API methods for the hook
 */
const API = Object.freeze({
  /**
   * Fetch datasources and documents for a collection by type
   */
  fetchDatasourcesAndDocuments: async (
    collection_id: string,
    datasourceTab: DatasourceTab,
    datasource_id?: string,
    document_id?: string
  ): Promise<DatasourcesAndDocumentsResponse> => {
    return callGet(
      datasourceEndpoints.datasourcesDocuments(
        collection_id,
        datasourceTab,
        datasource_id,
        document_id
      )
    );
  },
});

/**
 * Updates a specific document in the datasources documents cache
 * @param queryClient - The TanStack Query client
 * @param collection_id - The collection identifier
 * @param datasourceTab - The datasource tab type
 * @param document_id - The ID of the document to update
 * @param updatedDocument - The updated document object (partial or full)
 */
export const onDatasourcesDocumentsUpdated = async (
  queryClient: QueryClient,
  collection_id: string,
  datasourceTab: DatasourceTab,
  datasource_id?: string,
  document_id?: string
) => {
  const queryKey = queryKeyFactories.datasourcesDocuments(collection_id, datasourceTab);

  // Check if query is in cache before fetching
  if (!queryClient.getQueryData(queryKey)) {
    return;
  }

  try {
    // First, get the updated document data
    const response = await API.fetchDatasourcesAndDocuments(
      collection_id,
      datasourceTab,
      datasource_id,
      document_id
    );

    // If there are no documents, we don't need to update the cache
    if (!response.documents.length) {
      return;
    }

    // Upon this we will update either a document or a datasource
    const id = document_id || datasource_id;

    queryClient.setQueryData<DatasourcesAndDocumentsResponse>(queryKey, (oldData) => {
      if (!oldData) {
        return oldData;
      }

      // Get the first document from the response (assuming it's the updated one)
      const updatedRow = response.documents.find((document) => document.id === id);

      if (!updatedRow) {
        return oldData;
      }

      return {
        ...oldData,
        documents: oldData.documents.map((row) => {
          return row.id === id ? { ...row, ...updatedRow } : row;
        }),
      };
    });
  } catch (error) {
    // Silently fail as requested
    console.error('Failed to fetch datasource/document during cache update:', error);
  }
};

export const onDatasourcesDocumentsCreated = async (
  queryClient: QueryClient,
  collection_id: string,
  datasourceTab: DatasourceTab,
  datasource_id?: string,
  document_id?: string
) => {
  const queryKey = queryKeyFactories.datasourcesDocuments(collection_id, datasourceTab);

  // Check if query is in cache before fetching
  if (!queryClient.getQueryData(queryKey)) {
    return;
  }

  try {
    const { documents } = await API.fetchDatasourcesAndDocuments(
      collection_id,
      datasourceTab,
      datasource_id,
      document_id
    );

    const newRow = documents?.[0];

    // This means that the document was created in a folder
    if (!newRow) {
      return;
    }

    queryClient.setQueryData<DatasourcesAndDocumentsResponse>(queryKey, (oldData) => {
      if (!oldData) {
        return oldData;
      }

      return { ...oldData, documents: sortDatasourceDocuments([...oldData.documents, newRow]) };
    });
  } catch (error) {
    // Silently fail as requested
    console.error('Failed to fetch datasource/document during cache create:', error);
  }
};

export const onDatasourcesDocumentsDeleted = async (
  queryClient: QueryClient,
  collection_id: string,
  datasourceTab: DatasourceTab,
  datasource_id: string,
  document_id?: string
) => {
  const queryKey = queryKeyFactories.datasourcesDocuments(collection_id, datasourceTab);

  queryClient.setQueryData<DatasourcesAndDocumentsResponse>(queryKey, (oldData) => {
    if (!oldData) {
      return oldData;
    }

    // Delete document row
    if (document_id) {
      return {
        ...oldData,
        documents: oldData.documents.filter((document) => document.document_id !== document_id),
      };
    }

    // Delete folder row
    return {
      ...oldData,
      documents: oldData.documents.filter((document) => document.datasource_id !== datasource_id),
    };
  });
};

/**
 * Custom React hook for managing datasources and documents for collections using React Query
 *
 * This hook provides a unified interface for working with datasources and documents across
 * four different categories: FAQ, documents, files, and external sources. It uses React Query
 * for caching and state management with pessimistic updates.
 *
 * @param collection_id - The collection identifier
 * @param datasourceTab - One of: 'faq', 'documents', 'files', 'external_sources'
 * @param enabled - Whether the query should be enabled (defaults to true)
 *
 */
export const useAPIDatasourcesDocuments = ({
  collection_id,
  datasourceTab,
  enabled = true,
}: UseAPIDatasourcesDocumentsParams): UseAPIDatasourcesDocumentsReturn => {
  // Generate query key
  const queryKey = queryKeyFactories.datasourcesDocuments(collection_id, datasourceTab);

  // Fetch data
  const {
    data: response,
    isLoading: isDatasourcesDocumentsLoading,
    status: getStatus,
  } = useQuery<DatasourcesAndDocumentsResponse, Error>({
    queryKey,
    queryFn: () => API.fetchDatasourcesAndDocuments(collection_id, datasourceTab),
    enabled: !!collection_id && !!datasourceTab && enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes in milliseconds
  });

  const getDatasourceDocumentById = useCallback(
    (document_id: string) => {
      if (!document_id) {
        return;
      }

      return response?.documents.find((document) => document.document_id === document_id);
    },
    [response]
  );

  return {
    data: response?.documents || [],
    pagination: response?.pagination,
    isDatasourcesDocumentsLoading,
    getDatasourceDocumentById,
    getStatus,
  };
};
